/**
 * Tiny floating-badge component for the Admin Dashboard's "Pending
 * Approvals" tile. Polls /api/admin/approvals/count every 20 s and
 * shows the badge + pulsing ring when there's anything awaiting
 * review. Hidden when the count is zero so the dashboard stays
 * uncluttered on quiet days.
 *
 * Click → navigate to /admin/approvals.
 */
import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { ShieldAlert } from "lucide-react";
import api from "@/lib/api";

export default function ApprovalsBadge({ className = "" }) {
  const [count, setCount] = useState(0);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/admin/approvals/count");
      setCount(res.data.pending || 0);
    } catch {
      // Silently ignore — the badge is a glance UX, not critical.
    }
  }, []);

  useEffect(() => {
    load();
    const id = setInterval(load, 20000);
    return () => clearInterval(id);
  }, [load]);

  if (count === 0) return null;

  return (
    <Link
      to="/admin/approvals"
      className={`relative inline-flex items-center gap-2 bg-amber-500/15 hover:bg-amber-500/25 text-amber-300 border border-amber-500/30 px-3 py-1.5 text-xs font-body uppercase tracking-wider transition-colors ${className}`}
      data-testid="approvals-badge"
    >
      <span className="relative flex items-center">
        <ShieldAlert size={13} />
        <span className="absolute -top-1 -right-1 w-2 h-2 bg-amber-300 rounded-full animate-ping" />
      </span>
      <span data-testid="approvals-badge-count">{count}</span> pending
    </Link>
  );
}
